import React, { useEffect, useState } from "react";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Save, Building2 } from "lucide-react";

const EDIT_FIELDS = [
    ["company_name", "Company name"],
    ["contact_email", "Contact email"],
    ["telephone", "Telephone"],
    ["bank_account_name", "Account holder"],
    ["bank_sort_code", "Sort code"],
    ["bank_account_number", "Account number"],
];

const AdminBrands = () => {
    const [brands, setBrands] = useState([]);
    const [open, setOpen] = useState(null);
    const [form, setForm] = useState({});
    const [loginEmail, setLoginEmail] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get("/admin/brands").then((r) => setBrands(r.data));
    useEffect(() => { load(); }, []);

    const expand = async (b) => {
        if (open === b.brand_id) { setOpen(null); return; }
        const r = await api.get(`/admin/brand/${b.brand_id}`);
        setForm(EDIT_FIELDS.reduce((a, [k]) => ({ ...a, [k]: r.data[k] || "" }), {}));
        setLoginEmail(r.data.login_email || "");
        setOpen(b.brand_id); // open only after data is loaded, so keystrokes aren't overwritten
    };

    const save = async (brand_id) => {
        setSaving(true);
        try {
            await api.put(`/admin/brand/${brand_id}`, form);
            toast.success("Brand details updated");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save changes");
        } finally { setSaving(false); }
    };

    return (
        <DashboardShell role="admin" title="Brands" subtitle={`Manage brand account details · ${brands.length} brands`}>
            <div className="space-y-3">
                {brands.map((b) => (
                    <div key={b.brand_id} className="yc-card-sm bg-white" data-testid={`brand-row-${b.brand_id}`}>
                        <div className="p-4 flex flex-wrap items-center gap-3">
                            <Building2 className="w-5 h-5 text-yc-blue" />
                            <div className="flex-1 min-w-[180px]">
                                <div className="font-heading font-bold">{b.company_name}</div>
                                <div className="text-xs text-yc-ink/60">{b.email}</div>
                            </div>
                            <button data-testid={`brand-expand-${b.brand_id}`} onClick={() => expand(b)} className="yc-btn-secondary text-xs py-1.5 px-3">
                                {open === b.brand_id ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />} Edit details
                            </button>
                        </div>
                        {open === b.brand_id && (
                            <div className="border-t-2 border-yc-ink p-4 bg-yc-cream space-y-4" data-testid={`brand-edit-form-${b.brand_id}`}>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    {EDIT_FIELDS.map(([k, label]) => (
                                        <div key={k}>
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-yc-ink/50">{label}</div>
                                            <input data-testid={`brand-edit-${k}`} className="yc-input mt-1 text-sm" value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                                        </div>
                                    ))}
                                </div>
                                <div className="text-xs text-yc-ink/50">Login email ({loginEmail}) can't be changed here — it's tied to their sign-in.</div>
                                <button data-testid={`brand-save-${b.brand_id}`} disabled={saving} onClick={() => save(b.brand_id)} className="yc-btn-primary text-sm py-2 px-4 disabled:opacity-50"><Save className="w-4 h-4 mr-2" /> Save changes</button>
                            </div>
                        )}
                    </div>
                ))}
                {brands.length === 0 && <div className="yc-card p-8 text-center text-yc-ink/60">No brands yet.</div>}
            </div>
        </DashboardShell>
    );
};

export default AdminBrands;
